/**
 * Slider/Carousel - Modern Implementation
 *
 * NOTE: This file contains a fallback for Slick carousel (jQuery dependency).
 * For best performance, consider migrating to Swiper.js (zero dependencies).
 *
 * Migration guide:
 * 1. Install: npm install swiper
 * 2. Import: import Swiper from 'swiper/bundle';
 * 3. Replace initialization below
 *
 * @package PixelBase Child
 * @since 3.1.0
 */

import { ready, $$, $ } from "./utils.js";

ready(() => {
  // Check if we have carousel elements
  const carousels = $$(
    ".owl-carousel, .content-split__images--carousel, .hero-block__images--carousel, .cards-block__cards--carousel",
  );

  if (carousels.length === 0) return;

  // Check if jQuery and Slick are available (legacy support)
  if (typeof jQuery !== "undefined" && typeof jQuery.fn.slick !== "undefined") {
    initSlickCarousel();
  } else {
    // Fallback: Basic native carousel or warning
    console.warn(
      "Slick carousel not available. Consider migrating to Swiper.js for better performance.",
    );
    initNativeCarousel(carousels);
  }
});

/**
 * Initialize Slick carousel (legacy jQuery-based)
 */
function initSlickCarousel() {
  const $ = window.jQuery;
  const $carousel = $(
    ".owl-carousel, .content-split__images--carousel, .hero-block__images--carousel, .cards-block__cards--carousel",
  );

  if ($carousel.length === 0) return;

  // Prevent CLS by setting explicit height before initialization
  $carousel.each(function () {
    const $this = $(this);

    // Only process if it hasn't been initialized yet
    if ($this.hasClass("slick-initialized")) return;

    const firstImage = $this.find("img").first();

    if (firstImage.length > 1) {
      // Set min-height based on image dimensions or natural height
      if (firstImage[0].complete && firstImage[0].naturalHeight > 0) {
        const height = firstImage.height();
        if (height > 0) {
          $this.css("min-height", height + "px");
        }
      } else {
        // Image not loaded yet, wait for it
        firstImage.on("load", function () {
          const height = $(this).height();
          if (height > 0) {
            $this.css("min-height", height + "px");
          }
        });
      }
    }
  });

  // Small delay to ensure images are processed
  setTimeout(() => {
    $carousel.not(".slick-initialized").each(function () {
      const $this = $(this);
      const isCardsCarousel = $this.hasClass("cards-block__cards--carousel");

      // Different config for cards carousel vs image carousels
      const config = isCardsCarousel
        ? {
            slidesToShow: 4,
            slidesToScroll: 1,
            vertical: false,
            arrows: true,
            dots: false,
            autoplay: false,
            adaptiveHeight: false,
            fade: false,
            cssEase: "ease-out",
            speed: 300,
            waitForAnimate: true,
            responsive: [
              {
                breakpoint: 1024,
                settings: {
                  slidesToShow: 3,
                },
              },
              {
                breakpoint: 768,
                settings: {
                  slidesToShow: 2,
                },
              },
              {
                breakpoint: 480,
                settings: {
                  slidesToShow: 1,
                },
              },
            ],
            prevArrow:
              '<button class="slick-prev" aria-label="Previous slide"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="12" viewBox="0 0 20 12" fill="none" aria-hidden="true"><path d="M20 10.1117L18.225 11.8867L10 3.66172L1.775 11.8867L0 10.1117L10 0.111718L20 10.1117Z" fill="#151820"/></svg></button>',
            nextArrow:
              '<button class="slick-next" aria-label="Next slide"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="12" viewBox="0 0 20 12" fill="none" aria-hidden="true"><path d="M20 1.88828L18.225 0.113281L10 8.33828L1.775 0.113281L0 1.88828L10 11.8883L20 1.88828Z" fill="#151820"/></svg></button>',
          }
        : {
            slidesToShow: 1,
            vertical: false,
            arrows: true,
            dots: false,
            autoplay: true,
            adaptiveHeight: false,
            fade: false,
            cssEase: "ease-out",
            speed: 300,
            waitForAnimate: true,
            prevArrow:
              '<button class="slick-prev" aria-label="Previous slide"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="12" viewBox="0 0 20 12" fill="none" aria-hidden="true"><path d="M20 10.1117L18.225 11.8867L10 3.66172L1.775 11.8867L0 10.1117L10 0.111718L20 10.1117Z" fill="#151820"/></svg></button>',
            nextArrow:
              '<button class="slick-next" aria-label="Next slide"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="12" viewBox="0 0 20 12" fill="none" aria-hidden="true"><path d="M20 1.88828L18.225 0.113281L10 8.33828L1.775 0.113281L0 1.88828L10 11.8883L20 1.88828Z" fill="#151820"/></svg></button>',
          };

      $this.slick(config);
    });
  }, 50);
}

/**
 * Initialize native CSS-based carousel (no dependencies)
 */
function initNativeCarousel(carousels) {
  carousels.forEach((carousel) => {
    const slides = carousel.querySelectorAll("img, .slide");
    if (slides.length <= 1) return; // No need for carousel with 1 slide

    let currentSlide = 0;

    // Wrap slides in proper structure
    carousel.style.position = "relative";
    carousel.style.overflow = "hidden";

    slides.forEach((slide, index) => {
      slide.style.position = "absolute";
      slide.style.top = "0";
      slide.style.left = "0";
      slide.style.width = "100%";
      slide.style.opacity = index === 0 ? "1" : "0";
      slide.style.transition = "opacity 0.3s ease";
    });

    // Create navigation buttons
    const prevBtn = document.createElement("button");
    prevBtn.className = "carousel-prev";
    prevBtn.setAttribute("aria-label", "Previous slide");
    prevBtn.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="12" viewBox="0 0 20 12" fill="none"><path d="M20 10.1117L18.225 11.8867L10 3.66172L1.775 11.8867L0 10.1117L10 0.111718L20 10.1117Z" fill="#151820"/></svg>';

    const nextBtn = document.createElement("button");
    nextBtn.className = "carousel-next";
    nextBtn.setAttribute("aria-label", "Next slide");
    nextBtn.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="12" viewBox="0 0 20 12" fill="none"><path d="M20 1.88828L18.225 0.113281L10 8.33828L1.775 0.113281L0 1.88828L10 11.8883L20 1.88828Z" fill="#151820"/></svg>';

    carousel.appendChild(prevBtn);
    carousel.appendChild(nextBtn);

    // Navigation logic
    function showSlide(index) {
      slides[currentSlide].style.opacity = "0";
      currentSlide = (index + slides.length) % slides.length;
      slides[currentSlide].style.opacity = "1";
    }

    prevBtn.addEventListener("click", (e) => {
      e.preventDefault();
      showSlide(currentSlide - 1);
    });

    nextBtn.addEventListener("click", (e) => {
      e.preventDefault();
      showSlide(currentSlide + 1);
    });

    // Auto-play
    setInterval(() => {
      showSlide(currentSlide + 1);
    }, 5000);
  });
}

if (typeof jQuery !== "undefined" && typeof jQuery.fn.slick !== "undefined") {
  jQuery(document).ready(function ($) {

    if (typeof jQuery !== "undefined" && typeof jQuery.fn.slick !== "undefined") {
      jQuery(document).ready(function ($) {

        $('.main-stag-block').each(function () {

          var $block = $(this);
          var $toggle = $block.find('.stage_toggle');
          var $dropdown = $block.find('.stage_dropdown ul');
          var $items = $block.find('.stage_dropdown li');
          var $sliders = $block.find('.stages-slider');

          var $sliders_tg = $(this).find('.stage_toggle span');
          var $sliders_txt = $(this).find('.stage_dropdown ul li:first').text();
          $sliders_tg.text($sliders_txt);

          // INIT: hide all sliders except first
          $sliders.hide().eq(0).show();

          // INIT: activate first li
          $items.removeClass('active').eq(0).addClass('active');

          // Toggle dropdown
          $toggle.on('click', function () {
            $dropdown.slideToggle(200);
          });

          // Click on li
          $items.on('click', function () {
            var $li = $(this);
            var index = $li.index();

            // Active class
            $items.removeClass('active');
            $li.addClass('active');

            // Update toggle text
            $toggle.find('span').text($li.text());

            // Hide dropdown
            $dropdown.slideUp(200);

            // Show corresponding slider
            $sliders.hide().eq(index).show();

            // Refresh slick (important)
            $sliders.eq(index).slick('setPosition');
          });

          // Init slick per block
          $sliders.each(function () {
            var $slider = $(this);

            if (!$slider.hasClass('slick-initialized')) {
              $slider.slick({
                slidesToShow: 1,
                slidesToScroll: 1,
                arrows: false,
                dots: true,
                infinite: false,
                adaptiveHeight: true
              });
            }
          });

        });

      });
    }

  });

  jQuery(document).ready(function ($) {

    $('.gallery-slider').each(function () {
      var $section = $(this);
      var $track = $section.find('.gallery-slider__track');

      if (!$track.length || $track.hasClass('slick-initialized')) return;

      $track.slick({
        slidesToShow: 2,
        slidesToScroll: 1,
        arrows: true,
        dots: false,
        infinite: true,
        centerMode: false,
        prevArrow: $section.find('.gallery-slider__btn--prev'),
        nextArrow: $section.find('.gallery-slider__btn--next'),
        responsive: [
          {
            breakpoint: 768,
            settings: {
              slidesToShow: 1,
            },
          },
        ],
      });
    });

    // Initialise each key-kilometer slider independently so its prev/next
    // arrows are scoped to its own block. Using global $('.km-prev') selectors
    // breaks navigation when a page has more than one slider (e.g. the Stage
    // Hosts pages, which have three) because every slider tries to bind to
    // every arrow.
    $('.km-slider-block').each(function () {
      var $block = $(this);
      var $slider = $block.find('.km-slider');

      if (!$slider.length || $slider.hasClass('slick-initialized')) return;

      $slider.slick({
        slidesToShow: 2,
        slidesToScroll: 1,
        arrows: true,
        prevArrow: $block.find('.km-prev'),
        nextArrow: $block.find('.km-next'),
        dots: false,
        infinite: true,
        speed: 500,
        responsive: [
          {
            breakpoint: 991,
            settings: {
              slidesToShow: 1
            }
          }
        ]
      });
    });

    var $newsSlider = $('.latest-news__slider').not('.latest-news__slider--static');
    if ($newsSlider.length) {
      $newsSlider.slick({
        slidesToShow: 4,
        slidesToScroll: 1,
        arrows: true,
        prevArrow: $('.news-prev'),
        nextArrow: $('.news-next'),
        dots: false,
        responsive: [
          {
            breakpoint: 1200,
            settings: { slidesToShow: 3 }
          },
          {
            breakpoint: 768,
            settings: { slidesToShow: 2 }
          },
          {
            breakpoint: 480,
            settings: { slidesToShow: 1 }
          }
        ]
      });
    }
    
  });
}